import { CacheResolver } from 'apollo-cache-inmemory';
import {
  restResourceResolver,
  batchRestResourceResolver,
} from './restResourceResolver';
import { singularize, firstLetterToUpper } from '../stringOperations';
import {
  RestResourceResolverArgs,
  BatchRestResourceResolverArgs,
} from '../types';

interface GetCacheKeyArgs {
  __typename: string;
  id: string;
}

interface Context {
  getCacheKey: (args: GetCacheKeyArgs) => string;
}

interface ResolverMap {
  [field: string]: unknown;
}

interface CacheRedirectMap {
  [field: string]: CacheResolver;
}

const cacheRedirectForRestResource = (name: string) => (
  _: unknown,
  { id }: RestResourceResolverArgs,
  { getCacheKey }: Context,
) => {
  if (!id) {
    return undefined;
  }

  const typename = singularize(firstLetterToUpper(name));
  return id && getCacheKey({ __typename: typename, id });
};

const cacheRedirectForBatchRestResource = (name: string) => (
  _: unknown,
  { ids }: BatchRestResourceResolverArgs,
  { getCacheKey }: Context,
) => {
  const typename = singularize(firstLetterToUpper(name));
  return ids.map((id) => getCacheKey({ __typename: typename, id }));
};

/**
 * Adds cacheRedirects (https://www.apollographql.com/docs/react/advanced/caching/#cache-redirects-with-cacheredirects)
 * for any restResourceResolvers in the resolver map. This will ensure that the item can be grabbed by the Apollo Cache
 * by querying for its id (rather than firing off an unnecessary REST request)
 *
 * @param resolvers A map of resolver functions, some of which should be restResourceResolver
 */
export const restResourceCacheRedirects = (
  queryResolvers: ResolverMap,
): CacheRedirectMap => {
  // Get the resolvers that use restResourceResolver
  const restResourceResolverEntries = Object.keys(queryResolvers).filter(
    (name) => queryResolvers[name] === restResourceResolver,
  );

  // For each of these resources, create a cache redirect that infers the typename+id
  // cache index for that resource based on it's name and the id passed as an argument
  const cacheRedirects: CacheRedirectMap = restResourceResolverEntries.reduce(
    (acc: CacheRedirectMap, name) => {
      acc[name] = cacheRedirectForRestResource(name);
      return acc;
    },
    {},
  );

  return cacheRedirects;
};

/**
 * Adds cacheRedirects (https://www.apollographql.com/docs/react/advanced/caching/#cache-redirects-with-cacheredirects)
 * for any batchRestResourceResolvers in the resolver map. This will ensure that the item can be grabbed by the Apollo Cache
 * by querying for their ids (rather than firing off an unnecessary REST request)
 *
 * @param resolvers A map of resolver functions, some of which should be batchRestResourceResolver
 */
export const batchRestResourceCacheRedirects = (
  queryResolvers: ResolverMap,
): CacheRedirectMap => {
  // Get the resolvers that use batchRestResourceResolver
  const batchRestResourceResolverEntries = Object.keys(queryResolvers).filter(
    (name) => queryResolvers[name] === batchRestResourceResolver,
  );

  // For each of these resources, create a cache redirect that infers the typename+id
  // cache index for that resource based on it's name and the id passed as an argument
  const cacheRedirects: CacheRedirectMap = batchRestResourceResolverEntries.reduce(
    (acc: CacheRedirectMap, name) => {
      acc[name] = cacheRedirectForBatchRestResource(name);
      return acc;
    },
    {},
  );

  return cacheRedirects;
};
